import { Suspense, lazy } from 'react';
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { AdminProvider } from "@/contexts/AdminContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Header } from "@/components/Header";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import GlobalSEO from "@/components/GlobalSEO";
import MaintenanceGuard from "./components/MaintenanceGuard";
import { Loader2 } from 'lucide-react';

// Critical pages - loaded eagerly for better LCP
import Home from "./pages/Home";

// Toasters - deferred to not block first paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Footer and Cookie Banner - below fold, lazy loaded
const Footer = lazy(() => import("@/components/Footer").then(m => ({ default: m.Footer })));
const CookieConsentBanner = lazy(() => import("./components/CookieConsentBanner"));

// Lazy loaded public pages
const Shop = lazy(() => import('./pages/Shop'));
const Product = lazy(() => import('./pages/Product'));
const ProductRedirect = lazy(() => import('./components/ProductRedirect'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const OrderTracking = lazy(() => import('./pages/OrderTracking'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const ReturnsRefund = lazy(() => import('./pages/ReturnsRefund'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const SubmitReview = lazy(() => import('./pages/SubmitReview'));

// Lazy loaded admin pages - never needed on storefront
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const ProductManager = lazy(() => import('./pages/admin/ProductManager'));
const CategoryManager = lazy(() => import('./pages/admin/CategoryManager'));
const OrderManager = lazy(() => import('./pages/admin/OrderManager'));
const BannerManager = lazy(() => import('./pages/admin/BannerManager'));
const SiteInfoEditor = lazy(() => import('./pages/admin/SiteInfoEditor'));
const AdminUserManager = lazy(() => import('./pages/admin/AdminUserManager'));
const StockManager = lazy(() => import('./pages/admin/StockManager'));
const StatusMessagesEditor = lazy(() => import('./pages/admin/StatusMessagesEditor'));
const EmailSettings = lazy(() => import('./pages/admin/EmailSettings'));
const ReviewManager = lazy(() => import('./pages/admin/ReviewManager'));
const ContentManager = lazy(() => import('./pages/admin/ContentManager'));
const DesignEditor = lazy(() => import('./pages/admin/DesignEditor'));
const MaintenanceSettings = lazy(() => import('./pages/admin/MaintenanceSettings'));
const HealthCheck = lazy(() => import('./pages/admin/HealthCheck'));
const ShippingSettings = lazy(() => import('./pages/admin/ShippingSettings'));
const SecuritySettings = lazy(() => import('./pages/admin/SecuritySettings'));
const DiscountCodeManager = lazy(() => import('./pages/admin/DiscountCodeManager'));
const queryClient = new QueryClient();

// Minimal loading fallback for Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Invisible fallback for deferred components (toasters, footer)
const NullFallback = () => null;

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <LanguageProvider>
          <CartProvider>
            <AdminProvider>
              {/* Toasters lazy-loaded to not block first paint */}
              <Suspense fallback={<NullFallback />}>
                <Toaster />
                <Sonner />
              </Suspense>
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <ScrollToTop />
                <GlobalSEO />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Admin routes - always accessible, lazy loaded */}
                    <Route path="/admin" element={<AdminLogin />} />
                    <Route path="/admin/dashboard" element={<Dashboard />} />
                    <Route path="/admin/products" element={<ProductManager />} />
                    <Route path="/admin/stock" element={<StockManager />} />
                    <Route path="/admin/categories" element={<CategoryManager />} />
                    <Route path="/admin/orders" element={<OrderManager />} />
                    <Route path="/admin/reviews" element={<ReviewManager />} />
                    <Route path="/admin/banner" element={<BannerManager />} />
                    <Route path="/admin/site-info" element={<SiteInfoEditor />} />
                    <Route path="/admin/maintenance" element={<MaintenanceSettings />} />
                    <Route path="/admin/status-messages" element={<StatusMessagesEditor />} />
                    <Route path="/admin/email-settings" element={<EmailSettings />} />
                    <Route path="/admin/users" element={<AdminUserManager />} />
                    <Route path="/admin/content" element={<ContentManager />} />
                    <Route path="/admin/design" element={<DesignEditor />} />
                    <Route path="/admin/health" element={<HealthCheck />} />
                    <Route path="/admin/shipping" element={<ShippingSettings />} />
                    <Route path="/admin/security" element={<SecuritySettings />} />
                    <Route path="/admin/discount-codes" element={<DiscountCodeManager />} />
                    
                    {/* Maintenance page - no guard */}
                    <Route path="/maintenance" element={<Maintenance />} />
                    
                    {/* Review submission - always accessible (bypasses maintenance) */}
                    <Route path="/recension/:token" element={<SubmitReview />} />
                    
                    {/* Public routes - protected by maintenance guard */}
                    <Route path="*" element={
                      <MaintenanceGuard>
                        <div className="flex flex-col min-h-screen">
                          <Header />
                          <main className="flex-1">
                            <Routes>
                              <Route path="/" element={<Home />} />
                              <Route path="/shop" element={<Shop />} />
                              {/* New deterministic product URLs: /products/{id}/{slug} */}
                              <Route path="/products/:id/:slug" element={<Product />} />
                              {/* Legacy redirects: old /products/:slug format and /product/:id */}
                              <Route path="/products/:slug" element={<ProductRedirect />} />
                              <Route path="/product/:id" element={<ProductRedirect />} />
                              <Route path="/about" element={<About />} />
                              <Route path="/contact" element={<Contact />} />
                              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                              <Route path="/terms-conditions" element={<TermsConditions />} />
                              <Route path="/return-refund" element={<ReturnsRefund />} />
                              <Route path="/cookie-policy" element={<CookiePolicy />} />
                              <Route path="/shipping-policy" element={<ShippingPolicy />} />
                              <Route path="/checkout" element={<Checkout />} />
                              <Route path="/order-confirmation" element={<OrderConfirmation />} />
                              <Route path="/track-order" element={<OrderTracking />} />
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </main>
                          {/* Footer lazy-loaded - below fold */}
                          <Suspense fallback={<NullFallback />}>
                            <Footer />
                          </Suspense>
                          {/* Cookie banner lazy-loaded - non-critical */}
                          <Suspense fallback={<NullFallback />}>
                            <CookieConsentBanner />
                          </Suspense>
                        </div>
                      </MaintenanceGuard>
                    } />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </AdminProvider>
          </CartProvider>
        </LanguageProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
